import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  IconButton,
  Checkbox,
  Button,
  Typography,
  Popover,
  InputAdornment,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { EVIDENCE_REQUEST_STATUS } from "../../../../../utils/enums";

const statusOptions = [
  { id: EVIDENCE_REQUEST_STATUS.KEY.OPEN, label: "Open" },
  { id: EVIDENCE_REQUEST_STATUS.KEY.CLOSE, label: "Closed" },
];

const EvidenceRequestStatusFilterPopover = ({
  onFilterChange,
  activeFilters = [],
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState({});

  const open = Boolean(anchorEl);
  const isFilterActive = activeFilters.length > 0;

  useEffect(() => {
    if (activeFilters.length > 0) {
      const initialSelected = {};
      activeFilters.forEach((status) => {
        initialSelected[status] = true;
      });
      setSelectedStatuses(initialSelected);
    } else {
      setSelectedStatuses({});
    }
  }, [activeFilters]);

  const filteredOptions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return statusOptions;
    return statusOptions.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [searchQuery]);

  const handleApplyFilter = () => {
    onFilterChange(Object.keys(selectedStatuses));
    setAnchorEl(null);
  };

  const handleResetFilter = () => {
    setSelectedStatuses({});
    onFilterChange([]);
    setAnchorEl(null);
  };

  const styles = {
    popover: {
      width: "180px",
      maxHeight: "300px",
      borderRadius: theme.spacing(1),
      boxShadow: theme.shadows[4],
      border: `1px solid ${theme.palette.divider}`,
      overflow: "hidden",
      backgroundColor: theme.palette.background.paper,
    },
    header: {
      padding: `${theme.spacing(1)} ${theme.spacing(1.5)} ${theme.spacing(
        0.75
      )} ${theme.spacing(1.5)}`,
      backgroundColor: theme.palette.grey[50],
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    searchInput: {
      "& .MuiOutlinedInput-root": {
        height: "32px",
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(0.75),
        fontSize: "12px",
        "& fieldset": { borderColor: theme.palette.grey[300] },
        "&:hover fieldset": { borderColor: theme.palette.primary.main },
        "&.Mui-focused fieldset": {
          borderColor: theme.palette.primary.main,
          borderWidth: "1px",
        },
      },
    },
    searchIcon: { fontSize: 16, color: theme.palette.grey[500] },
    content: {
      maxHeight: "160px",
      overflowY: "auto",
      backgroundColor: theme.palette.background.paper,
      "&::-webkit-scrollbar": { width: "4px" },
      "&::-webkit-scrollbar-track": { background: theme.palette.grey[100] },
      "&::-webkit-scrollbar-thumb": {
        background: theme.palette.grey[300],
        borderRadius: "2px",
      },
      "&::-webkit-scrollbar-thumb:hover": { background: theme.palette.grey[400] },
    },
    optionRow: {
      display: "flex",
      alignItems: "center",
      padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
      cursor: "pointer",
      transition: "background-color 0.15s ease",
      "&:hover": { backgroundColor: theme.palette.grey[50] },
    },
    optionText: { fontSize: "12px", color: theme.palette.text.secondary },
    footer: {
      padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)} ${theme.spacing(
        1
      )} ${theme.spacing(1.5)}`,
      backgroundColor: theme.palette.grey[50],
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    buttonRow: { display: "flex", gap: theme.spacing(0.75) },
    resetButton: {
      flex: 1,
      fontSize: "11px",
      fontWeight: 500,
      textTransform: "none",
      borderRadius: theme.spacing(0.5),
    },
    applyButton: {
      flex: 1,
      fontSize: "11px",
      fontWeight: 600,
      textTransform: "none",
      borderRadius: theme.spacing(0.5),
      backgroundColor: theme.palette.primary.main,
      "&:hover": { backgroundColor: theme.palette.primary.dark },
    },
    noResults: {
      textAlign: "center",
      padding: theme.spacing(2),
      fontSize: "12px",
      color: theme.palette.grey[500],
    },
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          ml: 0.5,
          padding: "4px",
          backgroundColor: isFilterActive ? theme.palette.grey[200] : "transparent",
          borderRadius: theme.spacing(1),
          "&:hover": {
            backgroundColor: isFilterActive
              ? theme.palette.grey[300]
              : theme.palette.grey[100],
          },
        }}
      >
        <FilterListIcon fontSize="small" />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: styles.popover }}
      >
        <Box sx={styles.header}>
          <TextField
            size="small"
            placeholder="Search options..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            variant="outlined"
            sx={styles.searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={styles.searchIcon} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={styles.content}>
          {filteredOptions.map((opt) => (
            <Box
              key={opt.id}
              sx={styles.optionRow}
              onClick={() =>
                setSelectedStatuses((prev) => {
                  const next = { ...prev };
                  if (next[opt.id]) delete next[opt.id];
                  else next[opt.id] = true;
                  return next;
                })
              }
            >
              <Checkbox
                size="small"
                checked={Boolean(selectedStatuses[opt.id])}
                sx={{
                  mr: 1,
                  color: theme.palette.grey[600],
                  padding: theme.spacing(0.25),
                  "&.Mui-checked": { color: theme.palette.primary.main },
                }}
              />
              <Typography sx={styles.optionText}>{opt.label}</Typography>
            </Box>
          ))}

          {filteredOptions.length === 0 && searchQuery && (
            <Typography sx={styles.noResults}>No items found</Typography>
          )}
        </Box>

        <Box sx={styles.footer}>
          <Box sx={styles.buttonRow}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleResetFilter}
              sx={styles.resetButton}
            >
              Reset
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApplyFilter}
              sx={styles.applyButton}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default EvidenceRequestStatusFilterPopover;

